import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Descriptions, Table, Tag, Space, Typography, Row, Col, Spin, message, Modal, Input, Select, DatePicker } from 'antd';
import { ArrowLeft, Edit, Trash, CreditCard } from 'lucide-react';
import dayjs from 'dayjs';
import { fetchPayableById, updatePayable, deletePayable, addPayablePayment, fetchVendors, fetchExpenseCategoriesList, fetchFacilities } from '../utils/api';
import { isViewer } from '../utils/auth';

const { Title } = Typography;

export default function PayableDetails({ currentUser = null }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [payable, setPayable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [facilities, setFacilities] = useState([]);

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Cheque', reference: '', date: dayjs() });

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});

    const totalFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

    const statusLabel = (s) => {
        const map = { pending: 'Pending', partially_paid: 'Partially Paid', paid: 'Paid', overdue: 'Overdue' };
        return map[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const paidAmount = (item) => (Array.isArray(item?.payments) ? item.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) : 0);
    const outstandingAmount = (item) => Math.max(0, (Number(item?.totalAmount) || 0) - paidAmount(item));

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [data, vendorList, categoryList, facilityList] = await Promise.all([
                    fetchPayableById(id),
                    fetchVendors().catch(() => []),
                    fetchExpenseCategoriesList().catch(() => []),
                    fetchFacilities().catch(() => [])
                ]);
                setPayable(data);
                setVendors(vendorList.vendors || vendorList || []);
                setExpenseCategories(categoryList || []);
                setFacilities(facilityList || []);
            } catch (e) {
                message.error('Failed to load payable details');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const handleDelete = async () => {
        Modal.confirm({
            title: 'Delete Payable',
            content: 'Are you sure you want to delete this payable? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deletePayable(id);
                    message.success('Payable deleted');
                    navigate('/payables');
                } catch (e) {
                    message.error('Failed to delete payable');
                }
            }
        });
    };

    const openEditModal = () => {
        setEditForm({
            vendor: payable.vendor?._id || '',
            invoiceNumber: payable.invoiceNumber || '',
            invoiceDate: payable.invoiceDate ? dayjs(payable.invoiceDate) : null,
            paymentDueDate: payable.paymentDueDate ? dayjs(payable.paymentDueDate) : null,
            expenseType: payable.expenseType || 'opex',
            serviceMonth: payable.serviceMonth || '',
            expenseCategory: payable.expenseCategory?._id || '',
            facility: payable.facility || '',
            poNumber: payable.poNumber || '',
            poDate: payable.poDate ? dayjs(payable.poDate) : null,
            tdsPercent: payable.tdsPercent || 0,
            baseAmount: payable.baseAmount || 0,
            gstAmount: payable.gstAmount || 0,
            totalAmount: payable.totalAmount || 0,
            notes: payable.notes || ''
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        try {
            const payload = {
                vendor: editForm.vendor,
                invoiceNumber: editForm.invoiceNumber,
                invoiceDate: editForm.invoiceDate?.format('YYYY-MM-DD') || null,
                paymentDueDate: editForm.paymentDueDate?.format('YYYY-MM-DD') || null,
                expenseType: editForm.expenseType,
                serviceMonth: editForm.serviceMonth || undefined,
                expenseCategory: editForm.expenseCategory || undefined,
                facility: editForm.facility || '',
                poNumber: editForm.poNumber || '',
                poDate: editForm.poDate?.format('YYYY-MM-DD') || null,
                tdsPercent: parseFloat(editForm.tdsPercent) || 0,
                baseAmount: parseFloat(editForm.baseAmount) || 0,
                gstAmount: parseFloat(editForm.gstAmount) || 0,
                totalAmount: parseFloat(editForm.totalAmount) || 0,
                notes: editForm.notes || ''
            };
            const updated = await updatePayable(id, payload);
            setPayable(updated);
            setShowEditModal(false);
            message.success('Payable updated');
        } catch (e) {
            message.error('Failed to update payable');
        }
    };

    const openPaymentModal = () => {
        setPaymentForm({ amount: '', method: 'Cheque', reference: '', date: dayjs() });
        setShowPaymentModal(true);
    };

    const handlePaymentSubmit = async () => {
        try {
            const payment = {
                amount: parseFloat(paymentForm.amount) || 0,
                method: paymentForm.method,
                reference: paymentForm.reference,
                date: paymentForm.date?.format('YYYY-MM-DD') || new Date().toISOString().slice(0, 10)
            };
            await addPayablePayment(id, payment);
            const updated = await fetchPayableById(id);
            setPayable(updated);
            setShowPaymentModal(false);
            message.success('Payment recorded');
        } catch (e) {
            message.error('Failed to record payment');
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!payable) {
        return (
            <div className="page-container">
                <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/payables')}>Back to Payables</Button>
                <Card style={{ marginTop: 16 }}>
                    <p>Payable not found</p>
                </Card>
            </div>
        );
    }

    const serviceMonthMap = {
        jan: 'January', feb: 'February', mar: 'March', apr: 'April',
        may: 'May', jun: 'June', jul: 'July', aug: 'August',
        sep: 'September', oct: 'October', nov: 'November', dec: 'December'
    };

    return (
        <div className="page-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/payables')}>Back to Payables</Button>
                <Space>
                    {!isViewer() && (
                        <>
                            <Button type="primary" icon={<CreditCard size={16} />} onClick={openPaymentModal}>Make Payment</Button>
                            <Button icon={<Edit size={16} />} onClick={openEditModal}>Edit</Button>
                        </>
                    )}
                    {currentUser?.role === 'admin' && (
                        <Button danger icon={<Trash size={16} />} onClick={handleDelete}>Delete</Button>
                    )}
                </Space>
            </div>

            <Card title="Payable Details">
                <Row gutter={[24, 16]}>
                    <Col span={8}>
                        <Typography.Text type="secondary">Vendor</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.vendor?.name || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Status</Typography.Text>
                        <Space style={{ display: 'flex', marginTop: 2 }}>
                            {payable.paymentStatus !== 'paid' && (
                                <Tag color={payable.status === 'overdue' ? 'error' : 'warning'}>
                                    {(payable.status || 'open').charAt(0).toUpperCase() + (payable.status || 'open').slice(1)}
                                </Tag>
                            )}
                            <Tag color={payable.paymentStatus === 'paid' ? 'success' : 'processing'}>
                                {statusLabel(payable.paymentStatus)}
                            </Tag>
                        </Space>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Invoice Number</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.invoiceNumber || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Invoice Date</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.invoiceDate ? new Date(payable.invoiceDate).toLocaleDateString() : '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Payment Due Date</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.paymentDueDate ? new Date(payable.paymentDueDate).toLocaleDateString() : '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Capex/Opex</Typography.Text>
                        <div style={{ fontSize: 14, textTransform: 'capitalize' }}>{payable.expenseType || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Service Month</Typography.Text>
                        <div style={{ fontSize: 14, textTransform: 'capitalize' }}>{serviceMonthMap[payable.serviceMonth] || payable.serviceMonth || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Category</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.expenseCategory?.name || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Facility</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.facility || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Is MSME</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.isMsme ? 'Yes' : 'No'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">PO Number</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.poNumber || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">PO Date</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.poDate ? new Date(payable.poDate).toLocaleDateString() : '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Base Amount</Typography.Text>
                        <div style={{ fontSize: 14 }}>{totalFormatter.format(payable.baseAmount || 0)}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">GST Amount</Typography.Text>
                        <div style={{ fontSize: 14 }}>{totalFormatter.format(payable.gstAmount || 0)}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">TDS %</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.tdsPercent || 0}%</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Total Amount</Typography.Text>
                        <div style={{ fontSize: 14 }}>{totalFormatter.format(payable.totalAmount || 0)}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Paid Amount</Typography.Text>
                        <div style={{ fontSize: 14 }}>{totalFormatter.format(paidAmount(payable))}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Outstanding</Typography.Text>
                        <div style={{ fontSize: 14, color: outstandingAmount(payable) > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 500 }}>
                            {totalFormatter.format(outstandingAmount(payable))}
                        </div>
                    </Col>
                    <Col span={24}>
                        <Typography.Text type="secondary">Notes</Typography.Text>
                        <div style={{ fontSize: 14 }}>{payable.notes || '-'}</div>
                    </Col>
                </Row>
            </Card>

            {Array.isArray(payable.payments) && payable.payments.length > 0 && (
                <Card title="Payments" style={{ marginTop: 16 }}>
                    <Table
                        dataSource={payable.payments}
                        rowKey={(_, idx) => idx}
                        pagination={false}
                        size="small"
                        columns={[
                            { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => d ? new Date(d).toLocaleDateString() : '-' },
                            { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (a) => totalFormatter.format(a || 0) },
                            { title: 'Method', dataIndex: 'method', key: 'method', render: (m) => m || '-' },
                            { title: 'Reference', dataIndex: 'reference', key: 'reference', render: (r) => r || '-' },
                        ]}
                    />
                </Card>
            )}

            {/* Payment Modal */}
            <Modal
                title="Make Payment"
                open={showPaymentModal}
                onCancel={() => setShowPaymentModal(false)}
                onOk={handlePaymentSubmit}
                okText="Record Payment"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <label>Amount</label>
                        <Input
                            type="number"
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            placeholder="Enter amount"
                        />
                    </div>
                    <div>
                        <label>Payment Method</label>
                        <Select
                            style={{ width: '100%' }}
                            value={paymentForm.method}
                            onChange={(v) => setPaymentForm({ ...paymentForm, method: v })}
                            options={[
                                { label: 'Cheque', value: 'Cheque' },
                                { label: 'Bank Transfer', value: 'Bank Transfer' },
                                { label: 'Cash', value: 'Cash' },
                                { label: 'UPI', value: 'UPI' },
                                { label: 'Other', value: 'Other' },
                            ]}
                        />
                    </div>
                    <div>
                        <label>Reference</label>
                        <Input
                            value={paymentForm.reference}
                            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                            placeholder="Cheque/UTR number"
                        />
                    </div>
                    <div>
                        <label>Date</label>
                        <DatePicker
                            style={{ width: '100%' }}
                            value={paymentForm.date}
                            onChange={(d) => setPaymentForm({ ...paymentForm, date: d })}
                            format="DD/MM/YYYY"
                        />
                    </div>
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Payable"
                open={showEditModal}
                onCancel={() => setShowEditModal(false)}
                onOk={handleEditSubmit}
                okText="Save"
                width={700}
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Vendor</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={editForm.vendor || undefined}
                                    onChange={(v) => setEditForm({ ...editForm, vendor: v })}
                                    options={vendors.map(v => ({ label: v.name, value: v._id }))}
                                    placeholder="Select vendor"
                                    showSearch
                                    optionFilterProp="label"
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Invoice Number</label>
                                <Input value={editForm.invoiceNumber} onChange={(e) => setEditForm({ ...editForm, invoiceNumber: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Invoice Date</label>
                                <DatePicker style={{ width: '100%' }} value={editForm.invoiceDate} onChange={(d) => setEditForm({ ...editForm, invoiceDate: d })} format="DD/MM/YYYY" />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Payment Due Date</label>
                                <DatePicker style={{ width: '100%' }} value={editForm.paymentDueDate} onChange={(d) => setEditForm({ ...editForm, paymentDueDate: d })} format="DD/MM/YYYY" />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Expense Type</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={editForm.expenseType}
                                    onChange={(v) => setEditForm({ ...editForm, expenseType: v })}
                                    options={[{ label: 'Opex', value: 'opex' }, { label: 'Capex', value: 'capex' }]}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Category</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={editForm.expenseCategory || undefined}
                                    onChange={(v) => setEditForm({ ...editForm, expenseCategory: v })}
                                    options={expenseCategories.map(c => ({ label: c.name, value: c._id }))}
                                    placeholder="Select category"
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Facility</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={editForm.facility || undefined}
                                    onChange={(v) => setEditForm({ ...editForm, facility: v })}
                                    options={facilities.map(f => ({ label: f.name, value: f.name }))}
                                    placeholder="Select facility"
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Base Amount</label>
                                <Input type="number" value={editForm.baseAmount} onChange={(e) => setEditForm({ ...editForm, baseAmount: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>GST Amount</label>
                                <Input type="number" value={editForm.gstAmount} onChange={(e) => setEditForm({ ...editForm, gstAmount: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>TDS %</label>
                                <Input type="number" value={editForm.tdsPercent} onChange={(e) => setEditForm({ ...editForm, tdsPercent: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Total Amount</label>
                                <Input type="number" value={editForm.totalAmount} onChange={(e) => setEditForm({ ...editForm, totalAmount: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>PO Number</label>
                                <Input value={editForm.poNumber || ''} onChange={(e) => setEditForm({ ...editForm, poNumber: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Notes</label>
                                <Input.TextArea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
                            </div>
                        </Col>
                    </Row>
                </div>
            </Modal>
        </div>
    );
}
